import { FC, useEffect, useRef, useState } from 'react';
import get from 'lodash/get';
import { generateQueriesResultNode, GRAPH_RESULT_FRAGMENT } from 'hooks/useLRQ/useLRQ.queries';
import { PlotQueryInput, QueryFrequency, QueryRequestInput, QueryType } from 'common/graphqlTypes';
import { generateEventQueryVariables, LRQRequest } from 'hooks/useLRQ/useLRQ';
import { buildUrl } from 'common/util/location';
import { getRequestId } from 'common/util/lrqRequest';
import * as K from 'common/constants';
import { Range, UrlParams } from 'common/types';
import LoadingPlaceholder from 'components/common/LoadingPlaceholder/LoadingPlaceholder';
import Link from 'components/common/Link/Link';
import { useEventCache } from 'hooks/useLRQ/useEventCache';
import LRQRequestManager from 'components/common/Managers/LRQRequestManager';
import { roundAverageVolume, roundDailyVolume } from '../../utils';
import { UsageGraph } from './UsageGraph';

import './UsageGraph.scss';

const GIGABYTES_PER_DAY_MULTIPLIER = 0.00008046627;

type GraphDetailsProps = {
  usagePerDay: number;
  handleDateRange?: (dateRange: Range) => void;
  retentionDays: number;
  isMasterWithSubsidiaries: boolean;
};

export const GraphDetails: FC<GraphDetailsProps> = ({
  usagePerDay,
  handleDateRange,
  retentionDays,
  isMasterWithSubsidiaries,
}: GraphDetailsProps) => {
  const [request, setRequest] = useState<LRQRequest | null>(null);
  const id = getRequestId(request, QueryType.PLOT);
  const result = useEventCache([id]);
  const graphRange = retentionDays < 30 ? retentionDays : 30;
  const dateRangeRef = useRef({} as Range); // stable reference

  // if logged in account is master billing account with subsidiary accounts show aggregate log volume in the billing usage graph
  const logVolumeQuery = isMasterWithSubsidiaries
    ? "sumPerSecond(value where tag='subsidiaryLogVolume' metric='logBytes')"
    : "sumPerSecond(value where tag='logVolume' metric='logBytes')";

  useEffect(() => {
    const logVolumeRequest: LRQRequest = {
      queriesResultNode: generateQueriesResultNode([GRAPH_RESULT_FRAGMENT]),
      queriesVariables: [
        generateEventQueryVariables({
          startTime: `${graphRange} days`,
          endTime: '',
          plotQuery: {
            expression: `(${logVolumeQuery}/1073741824) * 86400`,
            frequency: QueryFrequency.HIGH,
            slices: graphRange,
          } as PlotQueryInput,
          type: QueryType.PLOT,
        } as unknown as QueryRequestInput),
      ],
    };

    setRequest(logVolumeRequest);
  }, []);

  const graphButton = (): JSX.Element => {
    const searchParams = {
      [K.URL_PARAM.BAR_WIDTH]: '1 day',
      [K.URL_PARAM.FILTER]: `${logVolumeQuery} * ${GIGABYTES_PER_DAY_MULTIPLIER}`,
      [K.URL_PARAM.GRAPH_STYLE]: 'stacked_bar',
      [K.URL_PARAM.START_TIME]: `${graphRange} days`,
      [K.URL_PARAM.Y_SCALE]: 'linear',
    };
    const search = buildUrl(K.ROUTE.GRAPH, searchParams as UrlParams);

    return (
      <Link href={search} openInNewTab>
        See Current Data Usage
      </Link>
    );
  };

  const renderGraph = (): JSX.Element | null => {
    if (!result) {
      return <LoadingPlaceholder />;
    }
    const xAxisSlices: number[] = get(result, 'results[0].data.xAxis', []);
    const yAxisValues: number[] = get(result, 'results[0].data.plots[0].samples', []);
    const volumesData = yAxisValues.map((volume: number) => roundDailyVolume(volume));
    const overallVolume = yAxisValues.reduce((accumulator, volume) => accumulator + volume, 0);
    const averageVolumeData = overallVolume !== 0 ? roundAverageVolume(overallVolume / yAxisValues.length) : 0;
    if (result.results[0].isDone) {
      if (handleDateRange) {
        dateRangeRef.current.min = xAxisSlices[0];
        dateRangeRef.current.max = xAxisSlices[xAxisSlices.length - 1];
        handleDateRange(dateRangeRef.current);
      }
      return (
        <>
          <UsageGraph
            xAxis={xAxisSlices}
            yAxis={volumesData}
            usagePerDay={usagePerDay}
            overallVolume={averageVolumeData}
          />
          {graphButton()}
        </>
      );
    }
    return null;
  };
  return (
    <>
      {renderGraph()}
      {request && <LRQRequestManager key={request.key} request={request} />}
    </>
  );
};
